import API from "../urls/api";
import { generateHeader, generateNewAccessToken } from "./api";
import { authToken } from "../utils/storage/storage";
import { getQuota } from "../utils/component/fileManagers/fileManager.function";
import store from "../redux/store";
import {
    EnoughSpaceFilesInterface,
    FileUploadStateInterface,
    FolderUploadStateInterface,
    setEnoughSpaceFiles,
    setEnoughSpaceFileStatus,
    setEnoughSpaceModal,
    setFileUploadState,
    setFolderUploadState
} from "../redux/slices/uploadSlice";
import { v4 as uuidv4 } from "uuid";
import axios from "axios";

const requestDelay = 1000;
let retryCounter = 0;
const dispatch = store.dispatch;

export function startUpload(event: (data: any, status: number) => void, size: number) {
    let status: number;
    fetch(API.upload.start(), {
        method: "POST",
        headers: generateHeader({ "Content-Type": "application/json" }),
        body: JSON.stringify({
            obj_size: size
        })
    })
        .then(function (response) {
            status = response.status;
            if (authToken.get() && status === 401) {
                generateNewAccessToken().then(() => {
                    startUpload(event, size);
                });
            }
            if (status === 429) {
                setTimeout(() => {
                    startUpload(event, size);
                }, requestDelay);
            }
            return response.json();
        })
        .then(function (data) {
            event(data, status);
        })
        .catch((err) => {
            event(err, status);
        });
}

export function upload(
    event: (data: any, status: number, failed?: boolean) => void,
    url: string,
    resourcePart: any,
    controller: any,
    onProgress: any
) {
    let status: number;
    axios
        .put(url, resourcePart as any, {
            headers: generateHeader(),
            signal: controller.signal,
            onUploadProgress: onProgress
        })
        .then(function (response) {
            status = response.status;
            if (authToken.get() && status === 401) {
                generateNewAccessToken().then(() => {
                    upload(event, url, resourcePart, controller, onProgress);
                });
            }
            return response.headers;
        })
        .then(function (data) {
            event(data, status);
        })
        .catch((err) => {
            if (!status || status > 299 || status < 200) {
                if (err.name !== "AbortError" && err.name !== "CanceledError") {
                    setTimeout(() => {
                        if (retryCounter < 3) {
                            upload(event, url, resourcePart, controller, onProgress);
                            retryCounter++;
                        } else {
                            retryCounter = 0;
                            event(err, status, true);
                        }
                    }, requestDelay);
                }
            }
        });
}

export function completeUpload(event: (data: any, status: number) => void, body: any) {
    let status: number;
    fetch(API.upload.complete(), {
        method: "POST",
        body: JSON.stringify(body),
        headers: generateHeader({ "Content-Type": "application/json" })
    })
        .then(function (response) {
            status = response.status;
            if (authToken.get() && status === 401) {
                generateNewAccessToken().then(() => {
                    completeUpload(event, body);
                });
            }
            if (status === 429) {
                setTimeout(() => {
                    completeUpload(event, body);
                }, requestDelay);
            }
            return response.json();
        })
        .then(function (data) {
            event(data, status);
        })
        .catch((err) => {
            event(err, status);
        });
}

export function abortUpload(event: (data: any, status: number) => void, body: any) {
    let status: number;
    fetch(API.upload.abort(), {
        method: "DELETE",
        body: JSON.stringify(body),
        headers: generateHeader({ "Content-Type": "application/json" })
    })
        .then(function (response) {
            status = response.status;
            if (authToken.get() && status === 401) {
                generateNewAccessToken().then(() => {
                    abortUpload(event, body);
                });
            }
            if (status === 429) {
                setTimeout(() => {
                    abortUpload(event, body);
                }, requestDelay);
            }
            return response.json();
        })
        .then(function (data) {
            event(data, status);
        })
        .catch((err) => {
            event(err, status);
        });
}

export function getUploadPercent(totalSize: number, uploadedSize: number) {
    const percent = Math.round((uploadedSize * 100) / totalSize);
    return `${isNaN(percent) || percent > 100 ? "100" : percent}%`;
}

export function checkQuota(totalSize: number, singleFiles?: File[], folderFiles?: File[]) {
    return new Promise((resolve) => {
        getQuota().then((data: any) => {
            if (data.used + totalSize > data.total) {
                let space = data.used;
                let filteredSingleFiles = [];
                let filteredFolderFiles = [];
                if (singleFiles) {
                    for (let i = 0; i < singleFiles.length; i++) {
                        if (space + singleFiles[i].size < data.total) {
                            space += singleFiles[i].size;
                            filteredSingleFiles.push({ id: uuidv4(), status: true, file: singleFiles[i], folder: false });
                        } else {
                            filteredSingleFiles.push({ id: uuidv4(), status: false, file: singleFiles[i], folder: false });
                        }
                    }
                }
                if (folderFiles) {
                    for (let i = 0; i < folderFiles.length; i++) {
                        if (space + folderFiles[i].size < data.total) {
                            space += folderFiles[i].size;
                            filteredFolderFiles.push({ id: uuidv4(), status: true, file: folderFiles[i], folder: true });
                        } else {
                            filteredFolderFiles.push({ id: uuidv4(), status: false, file: folderFiles[i], folder: true });
                        }
                    }
                }
                handleSetEnoughSpaceModal(true);
                handleSetEnoughSpaceFiles({
                    singleFiles: singleFiles ? filteredSingleFiles : [],
                    folderFiles: folderFiles ? filteredFolderFiles : []
                });
            } else {
                resolve(true);
            }
        });
    });
}

export function handleSetFileUploadState(upload: FileUploadStateInterface) {
    dispatch(setFileUploadState(upload));
}

export function handleSetFolderUploadState(upload: FolderUploadStateInterface) {
    dispatch(setFolderUploadState(upload));
}

export function handleSetEnoughSpaceModal(status: boolean) {
    dispatch(setEnoughSpaceModal(status));
}

export function handleSetEnoughSpaceFiles(files: EnoughSpaceFilesInterface) {
    dispatch(setEnoughSpaceFiles(files));
}

export function handleSetEnoughSpaceFileStatus(id: string, folder: boolean = false) {
    dispatch(setEnoughSpaceFileStatus({ id, folder }));
}
